<template>
  <div class="adn-input-text">
    <label
      v-if="label"
      class="adn-input-text__label"
      :class="{
        'adn-input-text__label--disabled': disabled,
        'adn-input-text__label--error': errorMessage,
      }"
      :for="id"
    >
      {{ label }}
    </label>
    <div
      class="adn-input-text__wrapper"
      :class="{
        'adn-input-text__wrapper--disabled': disabled,
        'adn-input-text__wrapper--error': errorMessage,
      }"
    >
      <div
        class="adn-input-text__container"
        :class="{
          'adn-input-text__container--disabled': disabled,
        }"
      >
        <span
          v-if="iconLeft"
          class="adn-input-text__icon adn-input-text__icon-left adn-icon"
          :class="iconLeftClassNames"
          aria-hidden="true"
        />
        <input
          :id="id"
          :name="name"
          :required="required"
          type="text"
          :value="modelValue"
          :placeholder="placeholder"
          class="adn-input-text__input"
          :class="classNames"
          :disabled="disabled"
          :aria-invalid="errorMessage ? 'true' : false"
          :aria-describedby="errorMessage ? `error${id}` : undefined"
          @input="updateValue($event)"
        />
        <span
          v-if="iconRight"
          class="adn-input-text__icon adn-input-text__icon-right adn-icon"
          :class="[iconRightClassNames, { 'adn-input-text__icon--clickable': hasRightClick }]"
          aria-hidden="true"
          data-test-icon-right
          @click="iconRightClick"
        />
      </div>
    </div>
    <div v-if="errorMessage" class="adn-input-text__error" data-test-error>
      <span class="adn-icon adn-icon--status-unavailable" aria-hidden="true" />
      <span :id="`error${id}`">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import uuidv4 from '@b2ag/utils/src/uuid'

export default defineComponent({
  name: 'AdnInputText',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    label: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconLeft: {
      type: String,
    },
    iconRight: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    error: {
      type: String,
      default: '',
    },
    validate: {
      type: Function,
      default: () => null,
    },
    hasRightClick: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({ validationError: '', id: uuidv4() }),
  computed: {
    classNames() {
      const classNames: string[] = []
      if (this.disabled) {
        classNames.push('adn-input-text__input--disabled')
      }
      if (this.iconLeft) {
        classNames.push('adn-input-text__input-left-content')
      }
      if (this.iconRight) {
        classNames.push('adn-input-text__input-right-content')
      }
      return classNames
    },
    iconLeftClassNames() {
      const classNames = [`adn-icon--${this.iconLeft}`]
      if (this.disabled) {
        classNames.push('adn-input-text__icon--disabled')
      }
      if (this.errorMessage) {
        classNames.push('adn-input-text__icon--error')
      }
      return classNames
    },
    iconRightClassNames() {
      const classNames = [`adn-icon--${this.iconRight}`]
      if (this.disabled) {
        classNames.push('adn-input-text__icon--disabled')
      }
      if (this.errorMessage) {
        classNames.push('adn-input-text__icon--error')
      }
      return classNames
    },
    errorMessage() {
      return this.error || this.validationError
    },
  },
  methods: {
    updateValue(e) {
      this.validationError = this.validate(e.target.value)
      this.$emit('update:modelValue', e.target.value)
    },
    iconRightClick() {
      this.$emit('onIconRightClick')
    },
  },
})
</script>

<style scoped lang="scss">
.adn-input-text {
  position: relative;
  max-height: fit-content;
  max-width: 100%;

  &__label {
    display: block;
    text-align: left;
    height: 22px;
    margin-bottom: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &--disabled {
      color: var(--color-inactive-light);
    }

    &--error {
      color: var(--color-error);
    }
  }

  &__wrapper {
    max-width: inherit;
    transition: all 0.2s;
    overflow: hidden;
    box-shadow: none;
    text-align: center;
    color: var(--color-text);
    background-color: var(--color-background-light);
    border-radius: var(--border-radius-s);
    border: 1px solid var(--color-border-light);

    &:focus-within {
      border: 1px solid var(--color-border);
    }

    &--open {
      border-radius: var(--border-radius-s) var(--border-radius-s) 0 0;
    }

    &--disabled {
      box-shadow: none;
      border-color: var(--color-inactive-light);
      color: var(--color-inactive-light);

      &:hover {
        box-shadow: none;
      }
    }

    &--error {
      border-color: var(--color-error);
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &__container {
    margin-top: -1px;
    margin-bottom: -1px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &--disabled {
      cursor: not-allowed;
    }
  }

  &__icon {
    display: inline-block;
    width: 4rem;
    min-width: 4rem;
    height: 4rem;
    line-height: 4rem;
    text-align: center;

    &-left {
      flex-grow: 0;
    }

    &-right {
      flex-grow: 0;
    }

    &--error {
      color: var(--color-error);
    }

    &--disabled {
      cursor: not-allowed;
    }

    &--clickable {
      cursor: pointer;
    }

    + .adn-input-text__icon {
      margin-left: -1rem;
    }
  }

  &__input {
    flex-grow: 1;
    display: block;
    width: 100%;
    height: 4rem;
    line-height: 2rem;
    vertical-align: middle;
    border-radius: 0;
    border: none;
    font-size: 1em;
    color: var(--color-text);
    padding: 12px 0;
    outline: none;
    margin: auto 2rem auto 1rem;
    background-color: var(--color-background-light);

    &-left-content {
      margin-left: 0;
    }

    &-right-content {
      margin-right: 0;
    }

    &-label {
      text-align: left;
    }

    &--clickable {
      cursor: pointer;
    }

    &--disabled {
      cursor: not-allowed;
    }

    &--hidden {
      position: absolute;
      height: 1px;
      width: 1px;
      z-index: -1;
    }

    &--readonly {
      cursor: default;
    }

    &:focus-within {
      border: none;
    }

    &:focus {
      border: none;
    }

    &::-ms-clear {
      display: none;
    }

    &:placeholder-shown {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &::placeholder {
      font-size: 1em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
      color: var(--color-text-light);
    }

    &--disabled {
      color: var(--color-inactive-light);

      &::placeholder {
        color: var(--color-inactive-light);
      }
    }
  }

  &__error {
    color: var(--color-error);
    font-weight: bold;
  }
}
</style>
